import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Tooltip from '../../components/molecules/Tooltip';
import GeneralPopup, { PopupTypes } from '../../components/organisms/GeneralPopup';
import { Spinner, Table, TableCell } from '../../components/widgets';
import TableCellWithSubTextAndIcon from '../../components/widgets/TableCellWithSubTextAndIcon';
import { getUserData } from '../../selectors/user';
import { getOrgAgencies } from '../../selectors/agencies';
import { getAttributionAppsList } from '../../selectors/apps';
import { getReadOnly } from '../../selectors/permissions';
import { getIsAgency, isUserAdNetwork } from '../../selectors/user';
import { OSIcons } from '../../utils/OS';
import { getUseNewShelfOption } from '../../utils/adNetworks';
import { download } from '../../utils/export';
import { sortAlphabeticallyLowecase, sortDate, sortNumeric } from '../../utils/sortUtil';
import {
    DELETE_CONFIGURATION,
    GET_CONFIGURED_PARTNERS,
    GET_ORGANIZATION_OPTIONS,
    GET_PARTNER_CONFIGURATIONS_FOR_PARTNER,
} from '../queries';
import PartnerConfigService from '../service';
import { isEditMode, isViewMode } from '../utils';
import css from './AppSitesTable.css';
import { SHELF_MODE } from './shelf/PartnerConfigurationShelf';
import { trackPartnerConfigMixpanelEvent } from './shelf/utils';
import { partnerShape } from './types';

export const TABLE_DATA_TEST_ID = 'app-sites-table';

const AppSiteField = {
    OS: 'os',
    NAME: 'appName',
    CLICK_THROUGH_WINDOW: 'clickThroughWindow',
    OWNER: 'owner',
    LAST_MODIFIED: 'lastModified',
};

const getHoverTextForPostbackType = (isAttributed, isEvent) => {
    if (isAttributed) {
        return 'STATIC.PAGES.PARTNER_CONFIGURATION.ATTRIBUTED';
    }

    return isEvent
        ? 'STATIC.PAGES.PARTNER_CONFIGURATION.ALL_USERS_EVENT'
        : 'STATIC.PAGES.PARTNER_CONFIGURATION.ALL_USERS';
};

const getPostbackAttributedText = isSendAll =>
    isSendAll
        ? 'STATIC.PAGES.PARTNER_CONFIGURATION.ATTRIBUTED_TYPES.ALL_USERS'
        : 'STATIC.PAGES.PARTNER_CONFIGURATION.ATTRIBUTED_TYPES.ATTRIBUTED';

const shouldShowDeleteMode = (partnerConfiguration, isAgency, readOnly) =>
    // Advertisers can delete agency configs
    (!isAgency || partnerConfiguration.agencyName) && partnerConfiguration.siteId && !readOnly;

const getActionComponentsConfigs = (
    isAgency,
    isNewShelf,
    onActionClicked,
    onExportChangesHistoryClicked,
    onDeleteClicked,
    readOnly,
    orgAgencies,
    appsList,
    partnerName
) => {
    const iconProps = { variant: IconVariants.LIGHT, size: IconSizes.SMALL };

    return [
        {
            ActionComponent: props => <Icon name="eye" {...iconProps} {...props} />,
            key: 'viewConfiguration',
            title: 'viewConfiguration',
            onClick: configuration => onActionClicked(configuration, SHELF_MODE.VIEW),
            shouldShow: config =>
                config.siteId && isViewMode(config, isAgency, readOnly, orgAgencies, appsList, partnerName),
            tooltipText: 'STATIC.TOOLTIPS.VIEW',
            shouldTranslateToolTip: true,
        },
        {
            ActionComponent: props => <Icon name="copy" {...iconProps} {...props} />,
            key: 'duplicateConfiguration',
            title: 'duplicateConfiguration',
            onClick: configuration => onActionClicked(configuration, SHELF_MODE.DUPLICATE),
            tooltipText: 'STATIC.TOOLTIPS.DUPLICATE',
            shouldShow: config =>
                isNewShelf && isEditMode(config, isAgency, readOnly, orgAgencies, appsList, partnerName),
            shouldTranslateToolTip: true,
        },
        {
            ActionComponent: props => <Icon name="pencil" {...iconProps} {...props} />,
            key: 'editConfiguration',
            title: 'editConfiguration',
            onClick: configuration => onActionClicked(configuration, SHELF_MODE.EDIT),
            tooltipText: 'STATIC.TOOLTIPS.EDIT',
            shouldShow: config => isEditMode(config, isAgency, readOnly, orgAgencies, appsList, partnerName),
            shouldTranslateToolTip: true,
        },
        {
            ActionComponent: props => <Icon name="calendar" {...iconProps} {...props} />,
            key: 'exportChangesHistory',
            title: 'exportChangesHistory',
            onClick: onExportChangesHistoryClicked,
            shouldShow: () => true,
            tooltipText: 'STATIC.TOOLTIPS.EXPORT_CHANGES_HISTORY',
            shouldTranslateToolTip: true,
        },
        {
            ActionComponent: props => <Icon name="trash" {...iconProps} {...props} />,
            key: 'deleteConfiguration',
            title: 'deleteConfiguration',
            onClick: onDeleteClicked,
            shouldShow: config => shouldShowDeleteMode(config, isAgency, readOnly),
            tooltipText: 'STATIC.TOOLTIPS.DELETE',
            shouldTranslateToolTip: true,
        },
    ];
};

const getSortedFieldArrow = (field, sortOptions) => {
    if (sortOptions.field === field) {
        return sortOptions.ascending ? 'up' : 'down';
    }
    return null;
};

const getTableFieldProps = (fieldName, sortOptions = null, onSortChanged = null) => ({
    name: fieldName,
    headerAlign: 'center',
    boldHeader: true,
    ...(!sortOptions
        ? {}
        : {
              showHeaderArrow: true,
              headerArrow: getSortedFieldArrow(fieldName, sortOptions),
              headerClick: onSortChanged,
          }),
});

const normalizeAppsite = (appSite, rowData) => {
    if (!rowData) return [];

    return [
        {
            title: appSite.title,
            subText: appSite.subText,
            containerClassName: css.appsiteColumn,
            subTextClassName: css.subTextContainer,
            prefixIcons: [
                <img
                    className={css.appsiteIcon}
                    key={`appsite_${rowData.icon}`}
                    src={rowData.icon}
                    alt={rowData.icon}
                />,
                <Tooltip title={rowData[AppSiteField.OS]}>
                    {React.createElement(OSIcons[rowData[AppSiteField.OS]], {
                        title: rowData[AppSiteField.OS],
                        className: css.osIcon,
                    })}
                </Tooltip>,
            ],
        },
    ];
};

const getTableMetadata = (data, ldsSupported, sortOptions, onSortChanged, showOwnerColumn = false) => {
    const mandatoryFields = [
        {
            ...getTableFieldProps(AppSiteField.NAME, sortOptions, onSortChanged),
            displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.TABLE_COLUMNS.APP_SITE',
            cellComponent: TableCellWithSubTextAndIcon,
            cellValues: [
                {
                    propName: 'elements',
                    dataKey: 'site',
                    transformation: (appSite, index) => normalizeAppsite(appSite[0], data[index]),
                },
            ],
            cellProps: { className: css.appsiteRowCell },
        },
        {
            ...getTableFieldProps(AppSiteField.CLICK_THROUGH_WINDOW),
            displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.TABLE_COLUMNS.CLICK_THROUGH_WINDOW',
            cellComponent: TableCell,
            cellProps: { className: css.tableCellContainer },
        },
        {
            ...getTableFieldProps(),
            displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.TABLE_COLUMNS.POSTBACKS_TO_PARTNER',
            cellComponent: TableCellWithSubTextAndIcon,
            cellProps: { tdClassName: css.tableCellContainer, className: css.postbacksCellContainer },
            cellValues: [{ propName: 'elements', dataKey: 'postbacks' }],
        },
    ];

    const optionalFields = ldsSupported
        ? [
              {
                  ...getTableFieldProps(),
                  displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.TABLE_COLUMNS.PRIVACY',
                  cellComponent: TableCellWithSubTextAndIcon,
                  cellProps: {
                      tdClassName: classNames(css.tableCellContainer, css.privacyCell),
                      className: css.tableCellContainer,
                  },
                  cellValues: [{ propName: 'elements', dataKey: 'ldsElement' }],
              },
          ]
        : [];

    const ownerField = showOwnerColumn
        ? [
              {
                  ...getTableFieldProps(AppSiteField.OWNER, sortOptions, onSortChanged),
                  displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.TABLE_COLUMNS.OWNER',
                  cellComponent: TableCell,
                  cellProps: {
                      className: css.ownerCell,
                      containerClassName: css.ownerCellContainer,
                      textClassName: css.ownerCellText,
                      tdStyle: { width: '150px' },
                  },
                  headerWidth: '150px',
                  cellValues: [
                      { propName: 'value', dataKey: 'ownerName' },
                      { propName: 'rightCellElement', dataKey: 'ownerIcon' },
                  ],
              },
          ]
        : [];

    return [
        ...mandatoryFields,
        ...optionalFields,
        ...ownerField,
        {
            ...getTableFieldProps(AppSiteField.LAST_MODIFIED, sortOptions, onSortChanged),
            displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.TABLE_COLUMNS.LAST_EDITED',
            cellComponent: TableCell,
            cellProps: { className: css.osAndDateCell },
        },
    ];
};

const getLdsCell = (restrictLds, translate) => [
    {
        title: restrictLds
            ? translate('STATIC.PAGES.PARTNER_CONFIGURATION.RESTRICTED')
            : translate('STATIC.PAGES.PARTNER_CONFIGURATION.NOT_RESTRICTED'),
        titleClassName: restrictLds ? css.title : css.disabledTitle,
        subText: restrictLds ? translate('STATIC.PAGES.PARTNER_CONFIGURATION.LIMITED_DATA_SHARING') : null,
        subTextClassName: css.subText,
    },
];

const getPostbackElement = (title, isExists, isSendAll, isEvent, partnerName, translate) => ({
    title,
    titleClassName: isExists ? css.title : css.disabledTitle,
    subText: isExists ? translate(getPostbackAttributedText(isSendAll)) : null,
    cellTooltipKey: isExists ? getHoverTextForPostbackType(!isSendAll, isEvent) : null,
    cellTooltipData: isExists ? { partnerName } : null,
    subTextClassName: css.subText,
});

const isSendAll = postbacks => postbacks?.every(postback => postback.sendAll);

const getPostbacksCell = (configuredAppSite, partnerName, translate) => {
    const { install, reengagement, revenueEvents, customEvents } = configuredAppSite.postbacks;

    return [
        getPostbackElement(
            translate('STATIC.PAGES.PARTNER_CONFIGURATION.POSTBACK_TYPES.INSTALL'),
            install,
            install?.sendAll,
            false,
            partnerName,
            translate
        ),
        getPostbackElement(
            translate('STATIC.PAGES.PARTNER_CONFIGURATION.POSTBACK_TYPES.REENGAGEMENT'),
            reengagement,
            reengagement?.sendAll,
            false,
            partnerName,
            translate
        ),
        customEvents?.length
            ? {
                  title: translate('STATIC.PAGES.PARTNER_CONFIGURATION.POSTBACK_TYPES.EVENTS', {
                      eventsCount: customEvents.length,
                  }),
                  titleClassName: css.title,
                  headerTooltip: customEvents.map(postback => postback.eventName).join(', '),
                  subText: translate(getPostbackAttributedText(isSendAll(customEvents))),
                  subTextTooltipKey: getHoverTextForPostbackType(!isSendAll(customEvents), true),
                  subTextTooltipData: { partnerName },
                  subTextClassName: css.subText,
              }
            : {
                  title: translate('STATIC.PAGES.PARTNER_CONFIGURATION.POSTBACK_TYPES.NO_EVENTS'),
                  titleClassName: css.disabledTitle,
              },
        getPostbackElement(
            translate('STATIC.PAGES.PARTNER_CONFIGURATION.POSTBACK_TYPES.REVENUE'),
            revenueEvents?.length,
            isSendAll(revenueEvents),
            true,
            partnerName,
            translate
        ),
    ];
};

const getAppSiteCell = (data, appFilter, translate) => [
    {
        filter: appFilter,
        title: data.isCustom ? `${data.appName} (Custom)` : data.appName,
        titleClassName: css.title,
        subText: data.longname,
        subTextClassName: css.subText,
        cellTooltipKey: !data.siteId ? translate('STATIC.PAGES.PARTNER_CONFIGURATION.MISSING_SITE') : null,
    },
];
const getOwnerCell = (agencyName, agencyId, advertiserDisplayName, translate) => {
    if (agencyId) {
        const ownerName =
            agencyName && agencyName !== agencyId ? `${agencyName} (${agencyId})` : agencyName || agencyId;
        return {
            ownerName,
            ownerIcon: { icon: <Icon name="briefcase" size={IconSizes.SMALL} className={css.ownerIcon} />, text: '' },
        };
    }
    return {
        ownerName: advertiserDisplayName || translate('STATIC.PAGES.AGENCY_SETUP_DROPDOWN.LABEL'),
        ownerIcon: null,
    };
};
const getFormattedPartnerConfigurations = (sites, advertiserDisplayName, translate) =>
    sites.map(site => {
        const ownerCell = getOwnerCell(site.agencyName, site.agencyId, advertiserDisplayName, translate);

        return {
            ...site,
            ...ownerCell,
            appName: site.appName || 'Unavailable',
            clickThroughWindow: site.windows?.installClickWindowDeterministic,
        };
    });

const sortPartnerConfigurations = (data, sortOptions) => {
    const { field, ascending } = sortOptions;
    if (field === AppSiteField.CLICK_THROUGH_WINDOW) {
        return data.sort((a, b) => sortNumeric(a[field], b[field], ascending));
    } else if (field === AppSiteField.LAST_MODIFIED) {
        return data.sort((a, b) => sortDate(a[field], b[field], ascending));
    } else if (field === AppSiteField.OWNER) {
        return data.sort((a, b) => sortAlphabeticallyLowecase(a.ownerName, b.ownerName, !ascending));
    }

    return data.sort((a, b) => sortAlphabeticallyLowecase(a[field], b[field], !ascending));
};

function AppSitesTable({
    partner,
    appFilter = '',
    agencyFilter = '',
    translate = () => {},
    onEditAppSite = () => {},
    onDuplicateAppSite = () => {},
    onAppSiteForPartner = null,
}) {
    const [sortOptions, setSortOptions] = useState({ field: AppSiteField.NAME, ascending: true });
    const [searchParams, setSearchParams] = useSearchParams();
    const [configForDeletion, setConfigForDeletion] = useState(null);
    const organizationOptionsResponse = useQuery(GET_ORGANIZATION_OPTIONS);
    const { ldsSupported = false } = organizationOptionsResponse.data || {};
    const isNewShelf = getUseNewShelfOption(organizationOptionsResponse.data, partner);

    const [deletePartnerConfiguration, deletePartnerConfigurationResponse] = useMutation(DELETE_CONFIGURATION, {
        variables: { partnerId: partner.id, siteId: configForDeletion?.siteId, agencyId: configForDeletion?.agencyId },
    });

    const partnerConfigurationsResponse = useQuery(GET_PARTNER_CONFIGURATIONS_FOR_PARTNER, {
        variables: { partnerId: partner.id },
    });
    const [, configuredPartnerResponse] = useLazyQuery(GET_CONFIGURED_PARTNERS);

    const isAgency = useSelector(state => getIsAgency(state));
    const isPartner = useSelector(state => isUserAdNetwork(state));
    const userData = useSelector(state => getUserData(state));
    // For partners, show the impersonated org name; for advertisers, show their own company name
    const advertiserDisplayName = isPartner
        ? userData?.impersonated_org_company_display_name || ''
        : userData?.company_display_name || '';
    const readOnly = useSelector(getReadOnly);
    const orgAgencies = useSelector(getOrgAgencies);
    const hasAgencies = orgAgencies?.length > 0;
    // Show owner column for advertisers with agencies OR for partners
    const showOwnerColumn = hasAgencies || isPartner;
    const appsList = useSelector(getAttributionAppsList);

    // we got back from angular modal after saving a partner configuration
    useEffect(() => {
        const refresh = searchParams.get('refresh');
        if (refresh) {
            partnerConfigurationsResponse.refetch();
            searchParams.delete('refresh');
            setSearchParams(searchParams);
        }
    }, [partnerConfigurationsResponse]);

    useEffect(() => {
        const { called, loading, data } = deletePartnerConfigurationResponse;
        if (!called || loading) {
            return;
        }

        if (data?.deletePartnerConfiguration?.ok) {
            partnerConfigurationsResponse.refetch();
            configuredPartnerResponse.refetch();
        }

        setConfigForDeletion(null);
        deletePartnerConfigurationResponse.reset();
    }, [deletePartnerConfigurationResponse]);

    const onSortChanged = field => {
        trackPartnerConfigMixpanelEvent('Sort', partner, null, { column: field });
        setSortOptions({ field, ascending: sortOptions.field === field ? !sortOptions.ascending : true });
    };

    const onActionClicked = ({ siteId, agencyId }, mode) => {
        trackPartnerConfigMixpanelEvent(`${mode} Clicked`, partner);
        if (onAppSiteForPartner) {
            onAppSiteForPartner(partner.id, siteId, agencyId, mode);
        } else if (mode === SHELF_MODE.DUPLICATE) {
            onDuplicateAppSite(partner.id, siteId, agencyId);
        } else {
            onEditAppSite(partner.id, siteId, agencyId);
        }
    };

    const onDeleteClicked = ({ siteId, agencyId }) => setConfigForDeletion({ siteId, agencyId });

    const onExportChangesHistoryClicked = async configuration => {
        trackPartnerConfigMixpanelEvent('Export Changes History Clicked', partner);
        const api = new PartnerConfigService();
        const { contents, filename } = await api.exportChangesHistory({
            partnerName: partner.originalDisplayName,
            longname: configuration.longname,
            platform: configuration.os,
            agencyId: configuration.agencyId,
        });

        const defaultFilename = `partner_configuration_changes_${new Date().toISOString()}.csv`;
        download(filename || defaultFilename, contents);
    };

    const onDeleteApproved = async () => {
        trackPartnerConfigMixpanelEvent('Delete', partner);
        await deletePartnerConfiguration();
    };

    if (partnerConfigurationsResponse.loading || organizationOptionsResponse.loading) {
        return <Spinner show expanded />;
    }

    const { partnerConfigurations = [] } = partnerConfigurationsResponse.data || {};

    // Apply app and agency filters
    let filteredPartnerConfigurations = partnerConfigurations.filter(({ appName, longname, agencyName, agencyId }) => {
        const appFilterString = appFilter.toLowerCase();
        const agencyFilterString = agencyFilter.toLowerCase();
        const matchesAppFilter =
            !appFilter ||
            appName?.toLowerCase().includes(appFilterString) ||
            longname?.toLowerCase().includes(appFilterString);
        const matchesAgencyFilter =
            !agencyFilter ||
            agencyName?.toLowerCase().includes(agencyFilterString) ||
            agencyId?.toLowerCase().includes(agencyFilterString);
        return matchesAppFilter && matchesAgencyFilter;
    });

    filteredPartnerConfigurations = getFormattedPartnerConfigurations(
        filteredPartnerConfigurations,
        advertiserDisplayName,
        translate
    );
    const sortedPartnerConfigurations = sortPartnerConfigurations([...filteredPartnerConfigurations], sortOptions);
    const tableMetadata = getTableMetadata(
        sortedPartnerConfigurations,
        ldsSupported,
        sortOptions,
        onSortChanged,
        showOwnerColumn
    );

    const tableData = sortedPartnerConfigurations.map(data => {
        return {
            ...data,
            disabled: !data.siteId,
            site: getAppSiteCell(data, appFilter, translate),
            clickThroughWindow: data.clickThroughWindow ? `${data.clickThroughWindow} Days` : null,
            postbacks: getPostbacksCell(data, partner.displayName, translate),
            trClass: data.agencyId && !isAgency ? css.agencyRow : null,
            lastModified: moment(data.lastModified).format('MMM D, YYYY'),
            ...(ldsSupported ? { ldsElement: getLdsCell(data.ldsOptions.restrictLds, translate) } : {}),
        };
    });

    return (
        <>
            <div className={css.sitesTable}>
                <Table
                    dataTestId={TABLE_DATA_TEST_ID}
                    data={tableData}
                    metadata={tableMetadata}
                    trClass={css.tableCell}
                    theadClass={css.tableHeader}
                    zebraTable={false}
                    actionComponentsConfigs={getActionComponentsConfigs(
                        isAgency,
                        isNewShelf,
                        onActionClicked,
                        onExportChangesHistoryClicked,
                        onDeleteClicked,
                        readOnly,
                        orgAgencies,
                        appsList,
                        partner.name
                    )}
                />
            </div>
            <GeneralPopup
                open={!!configForDeletion}
                type={PopupTypes.DELETE}
                title="STATIC.PAGES.PARTNER_CONFIGURATION.DELETE_CONFIGURATION_MESSAGE"
                acceptText="STATIC.BUTTONS.DELETE"
                onAccept={onDeleteApproved}
                onReject={() => setConfigForDeletion(null)}
                loading={deletePartnerConfigurationResponse.loading}
            />
        </>
    );
}

AppSitesTable.propTypes = {
    partner: partnerShape.isRequired,
    appFilter: PropTypes.string,
    agencyFilter: PropTypes.string,
    translate: PropTypes.func,
    onEditAppSite: PropTypes.func,
    onDuplicateAppSite: PropTypes.func,
    onAppSiteForPartner: PropTypes.func,
};

export default withLocalize(AppSitesTable);
